// statistics_clinic_03a_practice_position.md — 5 DIY cases covering rank
// (with a "how big is the gap" check) and quartiles/IQR (as a spread
// description, with two of the five previewing the outlier-fence check
// that Clinic 03 covers in full).

export const STATS_CLINIC_03A_TRACKER_ID = 'statistics-clinic-03a-practice-bank';

export const STATS_CLINIC_03A_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_03A_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Branch board pack',
    scenario: 'The regional director is preparing a board pack and wants the branch table ordered clearly, with the strongest branch at the top. She also wants to know whether the leader is only slightly ahead or clearly separated from the next branch before praising the branch manager publicly.',
    tables: [{
      columns: ['branch', 'quarterly_revenue'],
      rows: [['Riverside', '142000'], ['Highgate', '198000'], ['Oakwood', '165000'], ['Millbank', '210000'], ['Sandford', '130000']],
    }],
    answerGuide: {
      business_question: 'Which branch should top the board pack, and is the lead clear or marginal?',
      statistic_family: 'Position.',
      statistic_chosen: 'Rank, with the gap to 2nd place as a supporting check.',
      why_statistic: 'The director wants order plus a sense of how decisive the lead is, not just an average.',
      calculation: 'Sorted: Millbank 210000 (1), Highgate 198000 (2), Oakwood 165000 (3), Riverside 142000 (4), Sandford 130000 (5). Gap between 1st and 2nd = 210000 - 198000 = 12000 (about 6%).',
      result: 'Millbank ranks 1st, ahead of Highgate by 12000 (about 6%).',
      meaning: 'Millbank leads, but the gap to Highgate is not huge — a clear lead, not a landslide.',
      decision: 'Feature Millbank at the top, but acknowledge Highgate is close behind rather than implying a dominant win.',
      caution: "One quarter's rank does not prove sustained performance; check whether the gap holds over several quarters before public praise.",
    },
    ruleTerms: {
      business_question: ['top', 'board pack', 'lead'],
      statistic_family: ['position'],
      statistic_chosen: ['rank', 'gap'],
      why_statistic: ['order', 'decisive'],
      calculation: ['millbank', '210000', '198000', '12000'],
      result: ['millbank', '1st', '12000'],
      meaning: ['clear', 'not', 'landslide'],
      decision: ['millbank', 'highgate', 'close'],
      caution: ['one quarter', 'several quarters'],
    },
    criticalTerms: { calculation: ['Millbank'], result: ['Millbank'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Ticket target',
    scenario: 'The support manager is reviewing whether the helpdesk target should promise a normal response window or investigate unusually slow tickets separately. Most tickets are answered quickly, but one ticket took much longer than the rest, and the manager wants the middle of the service experience described honestly.',
    tables: [{
      columns: ['ticket_id', 'response_minutes'],
      rows: [['K001', '3'], ['K002', '4'], ['K003', '5'], ['K004', '6'], ['K005', '8'], ['K006', '9'], ['K007', '12'], ['K008', '14'], ['K009', '22']],
    }],
    answerGuide: {
      business_question: 'What does a normal response window look like for the helpdesk target?',
      statistic_family: 'Position.',
      statistic_chosen: 'Quartiles and IQR.',
      why_statistic: "Need a robust 'normal' window that isn't distorted by the one slow ticket.",
      calculation: 'Sorted: 3,4,5,6,8,9,12,14,22. Median = 8. Q1 = 4.5, Q3 = 13, IQR = 8.5. (Upper fence, previewing Clinic 03, = 13 + 1.5x8.5 = 25.75; the 22-minute ticket does not exceed this.)',
      result: 'Middle 50% of tickets fall between 4.5 and 13 minutes.',
      meaning: 'A typical ticket resolves around the 4.5-13 minute band; the slowest ticket (22 min) is high but within the normal spread.',
      decision: 'Promise a response window based on Q1-Q3 (4.5-13 minutes) for the target.',
      caution: 'Only 9 tickets sampled; confirm this window holds over a full period before publishing a target.',
    },
    ruleTerms: {
      business_question: ['normal window', 'response'],
      statistic_family: ['position'],
      statistic_chosen: ['quartile', 'iqr'],
      why_statistic: ['robust', 'distorted'],
      calculation: ['4.5', '13', '8.5'],
      result: ['4.5', '13', 'minutes'],
      meaning: ['typical', 'band'],
      decision: ['q1', 'q3'],
      caution: ['9 tickets', 'full period'],
    },
    criticalTerms: { calculation: ['4.5', '13'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Promotion pressure',
    scenario: "The sales director sees one rep at the top of this month's sales list and wants to announce a promotion. The HR partner asks for a more careful view: how far ahead is the leader, and is one month of ordering enough to justify a permanent career decision?",
    tables: [{
      columns: ['rep', 'monthly_sales'],
      rows: [['Femi', '41000'], ['Grace', '41500'], ['Hassan', '22000'], ['Ivy', '20500'], ['Jide', '19000']],
    }],
    answerGuide: {
      business_question: 'Who is ranked first this month, and is the lead over 2nd place decisive enough for a promotion decision?',
      statistic_family: 'Position.',
      statistic_chosen: 'Rank, with the gap to 2nd place.',
      why_statistic: 'HR needs to know if the "win" is a clear lead or a virtual tie before backing a career decision.',
      calculation: 'Sorted: Grace 41500 (1), Femi 41000 (2), Hassan 22000 (3), Ivy 20500 (4), Jide 19000 (5). Gap between 1st and 2nd = 41500 - 41000 = 500 (about 1.2%).',
      result: 'Grace ranks 1st, but only 500 (about 1.2%) ahead of Femi.',
      meaning: 'The "lead" is essentially a statistical tie between Grace and Femi, not a clear win.',
      decision: "Do not base a permanent promotion on this single month's rank alone — the gap to 2nd place is too small to be decisive.",
      caution: 'Rank alone hides how close the result is; always report the gap alongside the rank, especially before a career decision.',
    },
    ruleTerms: {
      business_question: ['ranked first', 'promotion'],
      statistic_family: ['position'],
      statistic_chosen: ['rank', 'gap'],
      why_statistic: ['tie', 'career'],
      calculation: ['grace', '41500', 'femi', '41000', '500'],
      result: ['grace', '1st', '500'],
      meaning: ['tie', 'not', 'clear win'],
      decision: ['do not', 'single month', 'gap'],
      caution: ['rank alone', 'hides'],
    },
    criticalTerms: { calculation: ['Grace'], result: ['Grace'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Warehouse promise',
    scenario: 'The warehouse manager wants to set a pick-and-pack promise for customers. Most orders are completed in a reasonable time, but there is one very slow order in the sample. The manager wants the normal middle window and a check on whether the slow order should be investigated separately.',
    tables: [{
      columns: ['order_id', 'pick_minutes'],
      rows: [['P001', '5'], ['P002', '6'], ['P003', '6'], ['P004', '7'], ['P005', '8'], ['P006', '9'], ['P007', '10'], ['P008', '13'], ['P009', '34']],
    }],
    answerGuide: {
      business_question: 'What is the normal pick-and-pack time to promise, and should the slow order be flagged?',
      statistic_family: 'Position.',
      statistic_chosen: 'Quartiles and IQR.',
      why_statistic: 'Need a robust normal window rather than a guess.',
      calculation: 'Sorted: 5,6,6,7,8,9,10,13,34. Median = 8. Q1 = 6, Q3 = 11.5, IQR = 5.5. (Upper fence, previewing Clinic 03, = 11.5 + 1.5x5.5 = 19.75; the 34-minute order EXCEEDS this fence.)',
      result: 'Middle 50% of orders fall between 6 and 11.5 minutes; the 34-minute order sits far outside that window.',
      meaning: 'A typical pick-and-pack sits around 6-11.5 minutes; 34 minutes is unusual and worth investigating separately (Clinic 03 covers the formal outlier check).',
      decision: 'Promise a pick time based on Q1-Q3 (6-11.5 minutes); flag the 34-minute order for review rather than folding it into the normal promise.',
      caution: 'Do not average the 34-minute order into the typical promise — it would overstate the normal pick time.',
    },
    ruleTerms: {
      business_question: ['pick-and-pack', 'promise'],
      statistic_family: ['position'],
      statistic_chosen: ['quartile', 'iqr'],
      why_statistic: ['robust'],
      calculation: ['6', '11.5', '5.5', '19.75'],
      result: ['6', '11.5', 'outside'],
      meaning: ['typical', 'unusual'],
      decision: ['q1', 'q3', 'flag'],
      caution: ['do not average', 'overstate'],
    },
    criticalTerms: { calculation: ['34'] },
  },
  {
    id: 'DIY05',
    title: 'DIY Practice 05: Delivery promise by region',
    scenario: 'A logistics company wants to advertise a delivery promise by region. North has one very slow delivery, while South looks steadier. The operations lead wants the middle-50% delivery window for each region before publishing the promise.',
    tables: [{
      columns: ['region', 'delivery_id', 'delivery_days'],
      rows: [
        ['North', 'N001', '2'], ['North', 'N002', '3'], ['North', 'N003', '3'], ['North', 'N004', '4'], ['North', 'N005', '5'],
        ['North', 'N006', '6'], ['North', 'N007', '8'], ['North', 'N008', '10'], ['North', 'N009', '20'],
        ['South', 'S001', '3'], ['South', 'S002', '4'], ['South', 'S003', '5'], ['South', 'S004', '5'], ['South', 'S005', '6'],
        ['South', 'S006', '6'], ['South', 'S007', '7'], ['South', 'S008', '8'], ['South', 'S009', '9'],
      ],
    }],
    answerGuide: {
      business_question: 'What delivery-day window should be advertised for each region?',
      statistic_family: 'Position.',
      statistic_chosen: 'Quartiles and IQR, per region.',
      why_statistic: 'Need a robust regional window rather than one company-wide figure that could hide regional differences.',
      calculation: 'North sorted 2,3,3,4,5,6,8,10,20: Q1 = 3, Q3 = 9, IQR = 6. South sorted 3,4,5,5,6,6,7,8,9: Q1 = 4.5, Q3 = 7.5, IQR = 3. (Previewing Clinic 03: North\'s upper fence = 9+1.5x6 = 18, and the 20-day delivery EXCEEDS it; South\'s upper fence = 7.5+1.5x3 = 12, and South\'s slowest, 9, does not exceed it.)',
      result: "North's middle-50% window is 3-9 days (with the 20-day delivery unusually far outside it); South's window is 4.5-7.5 days with nothing unusual.",
      meaning: "South's delivery window is both faster and steadier; North is broadly similar in the middle but has one genuinely unusual delivery.",
      decision: "Advertise separate promises per region — South's tighter 4.5-7.5 day window, and North's 3-9 day window with the 20-day case reviewed separately (Clinic 03 covers the formal outlier check).",
      caution: 'Do not blend North and South into one company-wide promise — their spread is different enough that a single number would misrepresent one region.',
    },
    ruleTerms: {
      business_question: ['delivery-day window', 'region'],
      statistic_family: ['position'],
      statistic_chosen: ['quartile', 'iqr'],
      why_statistic: ['regional', 'hide'],
      calculation: ['north', '3', '9', 'south', '4.5', '7.5'],
      result: ['3', '9', '4.5', '7.5'],
      meaning: ['south', 'faster', 'north', 'unusual'],
      decision: ['separate promises', 'review'],
      caution: ['blend', 'misrepresent'],
    },
    criticalTerms: { calculation: ['North', 'South'] },
  },
];

export const STATS_CLINIC_03A_CONFIG = {
  trackerId: STATS_CLINIC_03A_TRACKER_ID,
  resourceKey: 'statistics_clinic_03a_practice_position',
  rubricKey: 'statistics_position',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Position Cases',
  description: 'Each case is a business request. The statistic is deliberately not named in the title — read the scenario, identify what is really being asked, then show the maths. Your work autosaves to your own practice record.',
  introAlert: 'The final number is never enough — show how the statistic was chosen and how the result was derived before you write the decision.',
  lenses: STATS_CLINIC_03A_LENSES,
  workingField: null,
  items: STATS_CLINIC_03A_ITEMS,
};
